import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  Users, 
  Settings, 
  UsersRound, 
  Workflow, 
  UserCheck, 
  Heart, 
  LogOut,
  Search,
  MessageSquare,
  UserCog,
  Archive,
  Phone,
  Clock,
  AlertCircle,
  Calendar,
  FileText,
  FolderKanban,
  Building2,
  UserPlus,
  Activity,
  CheckSquare,
  GitBranch
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";

import { useAuth } from "@/contexts/AuthContext";
import LogoutConfirmDialog from "@/components/common/LogoutConfirmDialog";
import { useSMSCount } from "@/hooks/sms/useSMSCount";
import { useVoicemailCount } from "@/hooks/voicemail/useVoicemailCount";

// Role-based navigation configuration
const roleBasedMenus: Record<string, Array<{ title: string; url: string; icon: any }>> = {
  ED: [
    { title: "Dashboard", url: "/admin/dashboard", icon: LayoutDashboard },
    { title: "Users", url: "/admin/users", icon: Users },
    { title: "Program Type", url: "/admin/program-type", icon: Settings },
    { title: "Patient Groups", url: "/admin/patient-groups", icon: FolderKanban },
    { title: "Activities", url: "/admin/activity", icon: Activity },
    { title: "Workflow & Tasks", url: "/admin/workflow", icon: GitBranch },
    { title: "Facility Availability", url: "/admin/facility-availability", icon: Building2 },
    { title: "Search Patients", url: "/global-search", icon: Search },
    { title: "SMS", url: "/admin/sms", icon: MessageSquare },
    { title: "Add Patient", url: "/add-patient", icon: UserPlus },
  ],
  EA: [
    { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
    { title: "Search Patients", url: "/global-search", icon: Search },
    { title: "Manage Groups", url: "/group-operations/create", icon: FolderKanban },
    { title: "Manage ES", url: "/manage-es", icon: UserCog },
    { title: "Manage VPE", url: "/manage-vpe", icon: UserCog },
    { title: "Appointment Slots", url: "/appointment-slots", icon: Calendar },
    { title: "SMS", url: "/sms", icon: MessageSquare },
    { title: "Add Patient", url: "/add-patient", icon: UserPlus },
  ],
  ES: [
    { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
    { title: "Search Patients", url: "/global-search", icon: Search },
    { title: "Scheduled Appointments", url: "/scheduled-appointments", icon: Calendar },
    { title: "Call History", url: "/call-history", icon: Phone },
    { title: "SMS", url: "/sms", icon: MessageSquare },
    { title: "Add Patient", url: "/add-patient", icon: UserPlus },
  ],
  CA: [
    { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
    { title: "Search Patients", url: "/global-search", icon: Search },
    { title: "Transfer Patients", url: "/transfer-patients", icon: FileText },
    { title: "SMS", url: "/sms", icon: MessageSquare },
    { title: "Add Patient", url: "/add-patient", icon: UserPlus },
  ],
  CS: [
    { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
    { title: "Search Patients", url: "/global-search", icon: Search },
    { title: "Transfer Patients", url: "/transfer-patients", icon: FileText },
    { title: "Scheduled Appointments", url: "/scheduled-appointments", icon: Calendar },
    { title: "Add Patient", url: "/add-patient", icon: UserPlus },
  ],
  PA: [
    { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
    { title: "Search Patients", url: "/global-search", icon: Search },
    { title: "Appointment Slots", url: "/appointment-slots", icon: Calendar },
    { title: "SMS", url: "/sms", icon: MessageSquare },
    { title: "Add Patient", url: "/add-patient", icon: UserPlus },
  ],
  PE: [
    { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
    { title: "Search Patients", url: "/global-search", icon: Search },
    { title: "Scheduled Appointments", url: "/scheduled-appointments", icon: Calendar },
    { title: "Add Patient", url: "/add-patient", icon: UserPlus },
  ],
};

interface AdminSidebarProps {
  isCollapsed: boolean;
}

export const AdminSidebar = ({ isCollapsed }: AdminSidebarProps) => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const { smsCount } = useSMSCount();
  const { voicemailCount } = useVoicemailCount();

  const handleLogoutClick = () => {
    setShowLogoutDialog(true);
  };

  const handleLogoutConfirm = async () => {
    await logout();
    setShowLogoutDialog(false);
    navigate("/");
  };

  // Get menu items based on user role
  const userType = user?.type || 'ED';
  const sidebarItems = roleBasedMenus[userType] || roleBasedMenus.ED;

  const renderNavItem = (item: { title: string; url: string; icon: any }) => {
    const navItemId = `sidebar_nav_${item.title.toLowerCase().replace(/\s+/g, '_')}_link`;
    const IconComponent = item.icon;
    
    return (
      <NavLink
        id={navItemId}
        key={item.title}
        to={item.url}
        className={({ isActive }) =>
          `flex items-center transition-all duration-200 group relative ${
            isCollapsed 
              ? `justify-center h-10 ml-2 -mr-2 pr-2 rounded-l-xl ${
                  isActive
                    ? "bg-[#7c3aed] text-white"
                    : "text-gray-400 hover:bg-gray-100 hover:text-gray-600 mr-2 rounded-xl"
                }`
              : `gap-3 px-4 py-3 rounded-xl mx-2 ${
                  isActive
                    ? "bg-[#7c3aed] text-white shadow-sm"
                    : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                }`
          }`
        }
      >
        <div className="relative flex-shrink-0 flex items-center justify-center">
          <IconComponent className="h-5 w-5" strokeWidth={1.5} />
          
          {/* Show SMS count badge on icon when collapsed */}
          {item.title === "SMS" && smsCount > 0 && isCollapsed && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] flex items-center justify-center px-1 py-0 text-[9px] font-bold"
            >
              {smsCount > 99 ? '99+' : smsCount}
            </Badge>
          )}
          
          {/* Show Voicemail count badge on icon when collapsed */}
          {item.title === "Call History" && voicemailCount > 0 && isCollapsed && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] flex items-center justify-center px-1 py-0 text-[9px] font-bold"
            >
              {voicemailCount > 99 ? '99+' : voicemailCount}
            </Badge>
          )}
        </div>
        
        {!isCollapsed && (
          <>
            <span className="font-medium text-sm whitespace-nowrap">
              {item.title}
            </span>
            
            {/* Show SMS count badge inline when expanded */}
            {item.title === "SMS" && smsCount > 0 && (
              <Badge 
                variant="destructive" 
                className="min-w-[20px] h-5 flex items-center justify-center px-1.5 py-0 text-xs font-bold ml-auto"
              >
                {smsCount > 99 ? '99+' : smsCount}
              </Badge>
            )}
            
            {/* Show Voicemail count badge inline when expanded */}
            {item.title === "Call History" && voicemailCount > 0 && (
              <Badge 
                variant="destructive" 
                className="min-w-[20px] h-5 flex items-center justify-center px-1.5 py-0 text-xs font-bold ml-auto"
              >
                {voicemailCount > 99 ? '99+' : voicemailCount}
              </Badge>
            )}
          </>
        )}
      </NavLink>
    );
  };

  const renderLogoutButton = () => {
    return (
      <Button
        id="sidebar_logout_btn"
        variant="ghost"
        onClick={handleLogoutClick}
        className={`text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-all duration-200 ${
          isCollapsed 
            ? "h-10 ml-2 mr-2 p-0 rounded-xl flex items-center justify-center w-[calc(100%-1rem)]" 
            : "w-full justify-start gap-3 px-4 py-3 rounded-xl mx-2"
        }`}
      >
        <LogOut className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
        {!isCollapsed && (
          <span className="font-medium text-sm whitespace-nowrap">
            Logout
          </span>
        )}
      </Button>
    );
  };

  return (
    <aside 
      id="admin_sidebar_container"
      className={`${
        isCollapsed ? "w-[72px]" : "w-[280px]"
      } bg-white border-r border-gray-200 transition-all duration-300 h-[calc(100vh-4rem)] sticky top-16 flex flex-col`}
    >
      <ScrollArea className="flex-1">
        <nav id="admin_sidebar_nav" className={`py-6 space-y-2 ${isCollapsed ? "px-2" : "px-2"}`}>
          {sidebarItems.map((item) => renderNavItem(item))}
        </nav>
      </ScrollArea>
      
      <div className={`py-4 border-t border-gray-200 ${isCollapsed ? "px-2" : "px-2"}`}>
        {renderLogoutButton()}
      </div>

      <LogoutConfirmDialog 
        open={showLogoutDialog}
        onOpenChange={setShowLogoutDialog}
        onConfirm={handleLogoutConfirm}
      />
    </aside>
  );
};